import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Avatar,
  IconButton,
  TextField,
  Chip,
  Divider,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Button,
  useTheme,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import {
  Send as SendIcon,
  ArrowBack as ArrowBackIcon,
  AttachFile as AttachFileIcon,
  Delete as DeleteIcon,
  InsertPhoto as ImageIcon,
  MoreVert as MoreVertIcon,
  Block as BlockIcon,
  PersonOutline as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Assignment as AssignmentIcon,
  Campaign as CampaignIcon,
  Flag as FlagIcon,
  MarkChatRead as MarkChatReadIcon
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { format, formatDistanceToNow } from 'date-fns';
import pb from './pocketbase';

const SupportConversation = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  
  console.log('[SupportConversation] Render. userId from params:', userId);

  // State management
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [user, setUser] = useState(null);
  const [selectedImages, setSelectedImages] = useState([]);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [selectedMessage, setSelectedMessage] = useState(null);

  // Fetch messages and mark them as read
  useEffect(() => {
    const fetchMessages = async () => {
      setLoading(true);
      setError(null);
      try {
        // Fetch messages for this user, expand user info
        const result = await pb.collection('admin_chat').getList(1, 100, {
          filter: `user = "${userId}"`,
          sort: 'created',
          expand: 'user',
        });
        setMessages(result.items);
        // Set user info from the first message's expand.user
        if (result.items.length > 0 && result.items[0].expand && result.items[0].expand.user) {
          setUser(result.items[0].expand.user);
        } else {
          // fallback minimal user
          setUser({ id: userId, name: `User #${userId?.slice(0,8)}` });
        }
        // Mark unread messages as read
        const unreadMessages = result.items.filter(msg => msg.sender === 'user' && !msg.seen);
        if (unreadMessages.length > 0) {
          await Promise.all(unreadMessages.map(msg => pb.collection('admin_chat').update(msg.id, { seen: true })));
        }
      } catch (err) {
        setError('Failed to load conversation.');
        setMessages([]);
        setUser({ id: userId, name: `User #${userId?.slice(0,8)}` });
      } finally {
        setLoading(false);
      }
    };
    if (userId) fetchMessages();
    // Real-time subscription (optional, can keep as before)
    const unsubscribe = pb.collection('admin_chat').subscribe('*', function(e) {
      if (e.record && e.record.user === userId) {
        if (e.action === 'create') {
          setMessages(prev => [...prev, e.record]);
        } else if (e.action === 'update') {
          setMessages(prev => prev.map(msg => msg.id === e.record.id ? e.record : msg));
        } else if (e.action === 'delete') {
          setMessages(prev => prev.filter(msg => msg.id !== e.record.id));
        }
      }
    });
    return () => pb.collection('admin_chat').unsubscribe('*');
  }, [userId]);

  // Scroll to bottom when messages change
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Handle sending a new message
  const handleSendMessage = async (e) => {
    e?.preventDefault();
    
    if ((!newMessage || newMessage.trim() === '') && selectedImages.length === 0) {
      return; // Don't send empty messages
    }

    try {
      setSending(true);
      
      let imageFiles = [];
      if (selectedImages.length > 0) {
        imageFiles = [...selectedImages];
      }

      const messageData = {
        message: newMessage.trim(),
        user: userId,
        sender: 'support', // This is from admin/support
        seen: false // User hasn't seen it yet
      };

      if (imageFiles.length > 0) {
        // Need to add the files to the form data
        const formData = new FormData();
        
        // Add each image file
        imageFiles.forEach(file => {
          formData.append('image', file);
        });
        
        // Add other fields
        Object.keys(messageData).forEach(key => {
          formData.append(key, messageData[key]);
        });
        
        await pb.collection('admin_chat').create(formData);
      } else {
        // Simple message without images
        await pb.collection('admin_chat').create(messageData);
      }

      // Clear form after sending
      setNewMessage('');
      setSelectedImages([]);
    } catch (error) {
      console.error('Error sending message:', error);
      // Show error but don't clear form so user can retry
    } finally {
      setSending(false);
    }
  };

  // Handle file selection
  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);
    setSelectedImages(prevImages => [...prevImages, ...files]);
    // Clear the file input
    event.target.value = '';
  };

  // Remove a selected image
  const handleRemoveImage = (index) => {
    setSelectedImages(prevImages => 
      prevImages.filter((_, i) => i !== index)
    );
  };

  // Format date for messages
  const formatMessageDate = (dateString) => {
    const messageDate = new Date(dateString);
    const now = new Date();
    
    // Same day - show time only
    if (messageDate.toDateString() === now.toDateString()) {
      return format(messageDate, 'h:mm a');
    }
    
    // Within last week - show day and time
    if (now - messageDate < 7 * 24 * 60 * 60 * 1000) {
      return format(messageDate, 'EEEE h:mm a');
    }
    
    // Otherwise full date
    return format(messageDate, 'MMM d, yyyy h:mm a');
  };

  // Handle context menu for messages
  const handleMenuOpen = (event, message) => {
    event.preventDefault();
    setSelectedMessage(message);
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  // Delete a message
  const handleDeleteMessage = async () => {
    if (!selectedMessage) {
      handleMenuClose();
      return;
    }

    try {
      await pb.collection('admin_chat').delete(selectedMessage.id);
      // The subscription will update the UI
    } catch (error) {
      console.error('Error deleting message:', error);
    }
    
    handleMenuClose();
  };

  // Get user initials for avatar
  const getUserInitials = () => {
    if (!user) return '?';
    
    if (user.name && typeof user.name === 'string') {
      // Handle names like "User lckr90sa" from fallback
      if (user.name.startsWith('User ') && user.name.length > 5) return user.name[5].toUpperCase(); 
      return user.name.split(' ').map(n => n[0]).join('').toUpperCase();
    }
    
    if (user.email && typeof user.email === 'string') {
      return user.email[0].toUpperCase();
    }
    
    // Fallback for userId if name/email are not available
    if (userId && typeof userId === 'string' && userId.length > 0) {
        return userId.substring(0, Math.min(userId.length, 2)).toUpperCase();
    }
    return '?';
  };

  // Avatar URL logic
  let avatarUrl = '';
  if (user) {
    const collectionId = user.collectionId; // from PocketBase record
    const recordId = user.id;
    if (user.collectionName === 'sellers' && user.shop_logo) {
        avatarUrl = `${pb.baseUrl}/api/files/${collectionId}/${recordId}/${user.shop_logo}`;
    } else if (user.avatar) { // Assumes it's from 'users' or a similar auth collection with 'avatar' field
        // Default to _pb_users_auth_ if collectionId is not specific or for general users
        const avatarCollectionId = collectionId || '_pb_users_auth_'; 
        avatarUrl = `${pb.baseUrl}/api/files/${avatarCollectionId}/${recordId}/${user.avatar}`;
    }
  }

  return (
    <Box sx={{ height: 'calc(100vh - 80px)', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Paper sx={{ p: 2, borderRadius: '8px 8px 0 0', mb: 1 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item>
            <IconButton onClick={() => navigate(-1)}>
              <ArrowBackIcon />
            </IconButton>
          </Grid>
          <Grid item>
            <Avatar 
              alt={user?.name || 'User'}
              src={avatarUrl}
              sx={{ width: 48, height: 48, bgcolor: theme.palette.primary.main }}
            >
              {getUserInitials()}
            </Avatar>
          </Grid>
          <Grid item xs>
            <Typography variant="h6">
              {user?.name || (userId ? `User #${userId.substring(0, Math.min(userId.length,8))}` : 'Unknown User')}
            </Typography>
            {user?.email && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <EmailIcon fontSize="small" color="action" />
                <Typography variant="body2" color="text.secondary">
                  {user.email}
                </Typography>
              </Box>
            )}
          </Grid>
        </Grid>
      </Paper>

      {/* Message List */}
      <Paper 
        sx={{ 
          p: 2, 
          flex: 1, 
          overflowY: 'auto',
          bgcolor: theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.2)' : 'rgba(0, 0, 0, 0.02)'
        }}
      >
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        ) : messages.length === 0 ? (
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center',
            justifyContent: 'center', 
            height: '100%' 
          }}>
            <CampaignIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2, opacity: 0.5 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No messages yet
            </Typography>
            <Typography variant="body2" color="text.secondary" align="center" sx={{ maxWidth: 400 }}>
              Start the conversation by sending a message to this user
            </Typography>
          </Box>
        ) : (
          <Box sx={{ pb: 2 }}>
            {messages.map((message, index) => {
              const isFromUser = message.sender === 'user';
              const hasImages = message.image && message.image.length > 0;
              
              // Add date separator if it's first message or different day from previous
              const showDateSeparator = index === 0 || (
                new Date(message.created).toDateString() !== 
                new Date(messages[index - 1].created).toDateString()
              );

              return (
                <React.Fragment key={message.id}>
                  {showDateSeparator && (
                    <Box 
                      sx={{ 
                        display: 'flex', 
                        justifyContent: 'center', 
                        my: 2,
                        position: 'relative',
                      }}
                    >
                      <Divider sx={{ position: 'absolute', top: '50%', width: '100%', zIndex: 1 }} />
                      <Chip 
                        label={format(new Date(message.created), 'MMMM d, yyyy')} 
                        size="small"
                        sx={{ 
                          bgcolor: theme.palette.background.paper,
                          zIndex: 2,
                          boxShadow: '0px 0px 8px rgba(0, 0, 0, 0.1)'
                        }} 
                      />
                    </Box>
                  )}
                
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: isFromUser ? 'flex-start' : 'flex-end',
                      mb: 2,
                      position: 'relative'
                    }}
                    onContextMenu={(e) => handleMenuOpen(e, message)}
                  >
                    {isFromUser && (
                      <Avatar 
                        alt={user?.name || 'User'}
                        src={user?.avatar ? `${pb.baseUrl}/api/files/_pb_users_auth_/${userId}/${user.avatar}` : ''}
                        sx={{ 
                          width: 32, 
                          height: 32, 
                          mr: 1,
                          alignSelf: 'flex-end',
                          mb: 1,
                          bgcolor: theme.palette.primary.main
                        }}
                      >
                        {getUserInitials()}
                      </Avatar>
                    )}
                    
                    <Box sx={{ maxWidth: '70%' }}>
                      <Card
                        sx={{
                          bgcolor: isFromUser 
                            ? theme.palette.mode === 'dark' ? 'grey.800' : 'grey.100'
                            : theme.palette.primary.main,
                          color: isFromUser 
                            ? 'text.primary' 
                            : theme.palette.primary.contrastText,
                          borderRadius: isFromUser 
                            ? '0 18px 18px 18px'
                            : '18px 0 18px 18px',
                          boxShadow: 2,
                          position: 'relative',
                          p: 0
                        }}
                      >
                        <CardContent sx={{ p: '10px 16px', '&:last-child': { pb: '10px' } }}>
                          {message.message && message.message.trim() !== '' && (
                            <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                              {message.message}
                            </Typography>
                          )}
                          
                          {hasImages && (
                            <Box sx={{ mt: message.message ? 2 : 0, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                              {Array.isArray(message.image) && message.image.map((img, idx) => (
                                <Box
                                  key={idx}
                                  component="img"
                                  src={`${pb.baseUrl}/api/files/mtef6v4qokqh4pv/${message.id}/${img}`}
                                  alt={`Attachment ${idx + 1}`}
                                  sx={{
                                    maxWidth: '100%',
                                    maxHeight: 200,
                                    borderRadius: 1,
                                    cursor: 'pointer'
                                  }}
                                  onClick={() => window.open(`${pb.baseUrl}/api/files/mtef6v4qokqh4pv/${message.id}/${img}`, '_blank')}
                                />
                              ))}
                            </Box>
                          )}
                        </CardContent>
                      </Card>
                      
                      <Box sx={{ 
                        mt: 0.5, 
                        display: 'flex', 
                        justifyContent: isFromUser ? 'flex-start' : 'flex-end',
                        alignItems: 'center',
                        gap: 1
                      }}>
                        <Typography variant="caption" color="text.secondary">
                          {formatMessageDate(message.created)}
                        </Typography>
                        
                        {!isFromUser && (
                          <Tooltip title={message.seen ? "Seen" : "Not seen yet"}>
                            <MarkChatReadIcon 
                              fontSize="small" 
                              sx={{ 
                                fontSize: 14,
                                color: message.seen ? 'success.main' : 'text.disabled'
                              }} 
                            />
                          </Tooltip>
                        )}
                      </Box>
                    </Box>
                    
                    {!isFromUser && (
                      <Avatar 
                        sx={{ 
                          width: 32, 
                          height: 32, 
                          ml: 1,
                          alignSelf: 'flex-end',
                          mb: 1,
                          bgcolor: theme.palette.secondary.main
                        }}
                      >
                        S
                      </Avatar>
                    )}
                  </Box>
                </React.Fragment>
              );
            })}
            <div ref={messagesEndRef} />
          </Box>
        )}
      </Paper>

      {/* Message Input */}
      <Paper sx={{ p: 2, borderRadius: '0 0 8px 8px', mt: 1 }}>
        <form onSubmit={handleSendMessage}>
          {selectedImages.length > 0 && (
            <Box sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {selectedImages.map((file, index) => (
                <Box
                  key={index}
                  sx={{
                    position: 'relative',
                    width: 80,
                    height: 80,
                    borderRadius: 1,
                    overflow: 'hidden',
                    border: `1px solid ${theme.palette.divider}`,
                  }}
                >
                  <Box
                    component="img"
                    src={URL.createObjectURL(file)}
                    alt={`Selected image ${index}`}
                    sx={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                    }}
                  />
                  <IconButton
                    size="small"
                    sx={{
                      position: 'absolute',
                      top: 2,
                      right: 2,
                      bgcolor: 'rgba(0,0,0,0.5)',
                      color: 'white',
                      p: '2px',
                      '&:hover': {
                        bgcolor: 'rgba(0,0,0,0.7)',
                      }
                    }}
                    onClick={() => handleRemoveImage(index)}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              ))}
            </Box>
          )}
          
          <Grid container spacing={1} alignItems="center">
            <Grid item>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept="image/*"
                style={{ display: 'none' }}
                onChange={handleFileSelect}
              />
              <IconButton 
                color="primary" 
                onClick={() => fileInputRef.current.click()}
                disabled={sending}
              >
                <AttachFileIcon />
              </IconButton>
            </Grid>
            <Grid item xs>
              <TextField
                fullWidth
                placeholder="Type your message..."
                variant="outlined"
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                disabled={sending}
                multiline
                maxRows={4}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 4 } }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSendMessage();
                  }
                }}
              />
            </Grid>
            <Grid item>
              <Button
                variant="contained"
                color="primary"
                endIcon={<SendIcon />}
                disabled={sending && !newMessage.trim() && selectedImages.length === 0}
                type="submit"
                sx={{ borderRadius: 28, px: 3 }}
              >
                {sending ? <CircularProgress size={24} /> : 'Send'}
              </Button>
            </Grid>
          </Grid>
        </form>
      </Paper>

      {/* Context Menu for Messages */}
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
      >
        <MenuItem onClick={handleDeleteMessage}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Delete Message</ListItemText>
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default SupportConversation;